import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PreLoginTitleBar,
  Spinner,
} from 'components';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import { AuthAction, AuthStatus, useAuth } from 'providers/AuthProvider';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { trackError } from 'utils/trackError';
import { getApiErrorMessage, parseApiError } from 'utils/apiError';
import type { TFunction } from 'i18next';
import { MicrosoftSignInButton } from 'components/buttons/MicrosoftSignInButton';
import { toast } from 'sonner';
import { ErrorText, Subtitle, Title } from 'styles';
import { useNavigate, useSearchParams } from 'react-router';
import {
  CardCustomHeader,
  CardWrapper,
  Container,
  FieldsForm,
  InputContainer,
  LoginButtonsWrapper,
  Logo,
  Page,
} from './login.styles';

const loginSchema = (t: TFunction) =>
  z.object({
    email: z.email(t('login.valid_email')),
    password: z.string().min(1, t('login.password_required')),
  });

export default function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [isEntraLoading, setIsEntraLoading] = useState(
    () => sessionStorage.getItem('entraLoginPending') === 'true'
  );
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [displayLoginForm, setDisplayLoginForm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const loggedOut = searchParams.get('loggedOut') === 'true';
  const [showLoggedOutMessage] = useState(loggedOut ?? false);
  const sessionExpired = searchParams.get('sessionExpired') === 'true';
  const [showSessionExpiredMessage] = useState(sessionExpired ?? false);
  const showWorkstationMenu = useAudioDevice().mode === 'active';

  const emailInputRef = useRef<HTMLInputElement>(null);
  const loginForm = useForm({
    resolver: zodResolver(loginSchema(t)),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { login, entraLogin, authStatus, authError, lastAuthAction, isAuthenticated } = useAuth();

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (loggedOut) {
      setSearchParams(
        (params) => {
          params.delete('loggedOut');
          return params;
        },
        { replace: true }
      );
    }
  }, [loggedOut, setSearchParams]);

  useEffect(() => {
    if (sessionExpired) {
      setSearchParams(
        (params) => {
          params.delete('sessionExpired');
          return params;
        },
        { replace: true }
      );
    }
  }, [sessionExpired, setSearchParams]);

  useEffect(() => {
    if (authStatus === AuthStatus.Error && authError && lastAuthAction === AuthAction.EntraLogin) {
      toast.error(t('login.loginErrorMessageTechnical'));
      setIsEntraLoading(false);
      sessionStorage.removeItem('entraLoginPending');
    }
  }, [authStatus, authError, lastAuthAction, t]);

  useEffect(() => {
    if (isAuthenticated) {
      void navigate('/home', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLoginSubmit = loginForm.handleSubmit(async (data) => {
    setIsLoading(true);
    setErrorStatus(undefined);
    try {
      await login(data);
    } catch (error) {
      if (isAxiosError(error)) {
        const { status, code } = parseApiError(error);
        setErrorStatus(status);
        if (status === 401 || code === 'invalid_credentials') {
          loginForm.setError('root', {
            message: t('login.loginErrorMessage'),
          });
        } else if (status === 429) {
          loginForm.setError('root', {
            message: t('login.loginErrorMessage_429'),
          });
        } else {
          trackError(error, {
            context: 'login',
            status,
            code,
          });
          loginForm.setError('root', {
            message: getApiErrorMessage(error, t),
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  });

  const handleEntraLogin = () => {
    setIsEntraLoading(true);
    sessionStorage.setItem('entraLoginPending', 'true');
    void entraLogin();
  };

  const LoginButtons = () => {
    return (
      <LoginButtonsWrapper>
        <MicrosoftSignInButton
          variant="dark"
          className="h-12 rounded-md w-full text-lg font-medium"
          onClick={handleEntraLogin}
        />
        <Button
          type="button"
          variant="link"
          className="w-full text-sm font-medium text-primary h-8"
          onClick={() => {
            setDisplayLoginForm(true);
          }}
        >
          {t('login.login_with_password')}
        </Button>
      </LoginButtonsWrapper>
    );
  };

  return (
    <Page>
      {showWorkstationMenu && <PreLoginTitleBar hasBackButton={false} />}
      <Container>
        <Logo src="/images/logo-text-classic.svg" alt="Logo" />
        <CardWrapper>
          <Card className="rounded-sm border border-border shadow-xs gap-0 py-0">
            <CardContent className="flex flex-col gap-0 p-0">
              <CardCustomHeader>
                <Title className="text-xl font-semibold text-card-foreground mb-0">
                  {t('login.welcome')}
                </Title>
                {showSessionExpiredMessage ? (
                  <Subtitle className="text-sm text-warning">
                    {t('login.session_expired_message')}
                  </Subtitle>
                ) : showLoggedOutMessage ? (
                  <Subtitle className="text-sm text-success">
                    {t('login.logged_out_message')}
                  </Subtitle>
                ) : (
                  <Subtitle className="text-sm text-muted-foreground">
                    {displayLoginForm
                      ? t('login.enter_credentials')
                      : t('login.enter_microsoft_credentials')}
                  </Subtitle>
                )}
              </CardCustomHeader>
              {isEntraLoading ? (
                <div className="flex flex-col items-center gap-4 px-6 pb-6 pt-4">
                  <Spinner size="sm" />
                  <p className="text-sm text-muted-foreground">{t('login.microsoftLoggingIn')}</p>
                </div>
              ) : displayLoginForm ? (
                <div className="px-6 pb-6 pt-2">
                  <Form {...loginForm}>
                    <FieldsForm
                      aria-label={t('login.login_with_password')}
                      onSubmit={(e) => {
                        e.preventDefault();
                        void handleLoginSubmit(e);
                      }}
                    >
                      <FormField
                        control={loginForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('login.email')}</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                ref={emailInputRef}
                                type="email"
                                placeholder="ihre.email@beispiel.de"
                                autoComplete="email"
                                className="focus:ring-2 focus:ring-ring focus:border-ring-light"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={loginForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('login.password')}</FormLabel>
                            <FormControl>
                              <InputContainer>
                                <Input
                                  {...field}
                                  type={'password'}
                                  aria-label={t('login.password')}
                                  placeholder="Ihr Passwort"
                                  autoComplete="current-password"
                                  className="pr-10 focus:ring-2 focus:ring-ring focus:border-ring-light"
                                />
                              </InputContainer>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {loginForm.formState.errors.root?.message && (
                        <ErrorText role="alert" data-status={errorStatus}>
                          {loginForm.formState.errors.root.message}
                        </ErrorText>
                      )}
                      <div className="flex flex-col gap-3 w-full">
                        <Button
                          type="submit"
                          variant="default"
                          className="w-full h-12 rounded-md text-lg font-medium"
                          disabled={
                            (!loginForm.formState.isValid && errorStatus !== 429) || isLoading
                          }
                        >
                          {isLoading ? t('login.logging_in') : t('login.login_with_password')}
                        </Button>
                        <div className="flex items-center gap-3 w-full">
                          <div className="flex-1 border-t border-border" />
                          <span className="text-xs text-muted-foreground shrink-0">
                            {t('login.or')}
                          </span>
                          <div className="flex-1 border-t border-border" />
                        </div>
                        <MicrosoftSignInButton
                          variant="light"
                          className="h-12 rounded-md w-full text-lg font-medium"
                          onClick={handleEntraLogin}
                        />
                      </div>
                    </FieldsForm>
                  </Form>
                </div>
              ) : (
                <LoginButtons />
              )}
            </CardContent>
          </Card>
        </CardWrapper>
      </Container>
    </Page>
  );
}
